/**
 * 公用方法
 * */

import { enc, lib, MD5 } from 'crypto-js';
import dayjs from 'dayjs';
import { JSEncrypt } from 'jsencrypt';
import { isValidPhoneNumber } from 'libphonenumber-js/mobile';
import { DialogPlugin } from 'tdesign-react';
import { JSONParse, JSONStringify, LANG_TYPE, maskData, uuidv4 } from '@tencent/oneid-utils';
import APINames from 'common/api/APINames';
import RolesApi from 'common/api/servicesV3/RolesApi';
import { TApiError } from 'common/apiRequest/Request';
import {
  AUTHENTICATION_METHOD,
  BACKGROUND_TYPES,
  DEBOUNCE_DEFAULT_SECOND,
  getUnknownMistake,
  INVALID_CONTENT_ERROR_CODE,
  LOGIN_POSITIONS,
  POSITIONS,
} from 'common/constants/Constants';
import { IBrands } from 'common/constants/Types';
import { AdminApiV2 } from 'common/http/servicesV2';
import { ICertifyApi } from 'common/http/servicesV2/CertifyApi';
import CommonAccountApiV3 from 'common/http/servicesV3/AccountApi';
import i18n from 'common/locales';
import JSRuntime from 'common/utils/JSRuntime';

class Utils {
  public uniteClass(...args: any[]) {
    let classname = '';
    args.forEach((arg) => {
      if (typeof arg === 'string') {
        classname += ` ${arg}`;
      }
    });
    return classname.trim();
  }

  public noop() {}

  private getImageUrl(path: string, url: string): string {
    if (!url || !_.isString(url)) return url;

    if (/^blob:/.test(url) || /^https?:/.test(url)) return url;

    if (url.startsWith(`${JSRuntime.cdnUrl}/`)) return url;

    const trimPath = _.trim(path, '/');
    const pathArr = [JSRuntime.cdnUrl, 'images'];
    if (trimPath) pathArr.push(trimPath);
    pathArr.push(_.trimStart(url, '/'));
    return pathArr.join('/');
  }

  // 其他静态图片。约定，图片都包含完整路径包括扩展名
  public renderImage(url: string) {
    return this.getImageUrl('/web', url);
  }

  // 应用logo图片。约定，图片都包含完整路径包括扩展名
  public renderAppLogo(url: string): string {
    return this.getImageUrl('/', url);
  }

  // EID logo图片。约定，图片都包含完整路径包括扩展名
  public renderBrandLogo(url: string): string {
    return this.getImageUrl('/logo', url);
  }

  public renderLogo() {
    return this.renderBrandLogo('eid-logo.png');
  }

  public renderBrand() {
    return this.renderBrandLogo('eid-brand.png');
  }

  public renderTextBrand() {
    return this.renderBrandLogo(
      i18n.language === LANG_TYPE.EN_US ? 'oneid-text-en-US.png' : 'oneid-text.png',
    );
  }

  public renderTextBrandGray() {
    return this.renderBrandLogo(
      i18n.language === LANG_TYPE.EN_US ? 'oneid-text-en-US-gray.png' : 'oneid-text-gray.png',
    );
  }

  public renderBrandVertical() {
    return this.renderBrandLogo('eid-brand-vertical.png');
  }

  public renderCompanyLogo(url: string) {
    return this.renderImage(url || 'default-company-logo.png');
  }

  public resolvePromise(promise: Promise<any>): Promise<any> {
    return promise.then((data) => [null, data]).catch((err) => [err, null]);
  }

  /** 获取纯粹的hash路径 去除?和多重/等干扰 */
  public getPureHashPath(): string {
    const { hash = '' } = window.location;
    return (
      hash
        .split('?')?.[0]
        ?.split('/')
        .filter((item) => item)
        ?.join('/') || ''
    );
  }

  // 获取hash路径
  public getLocationHashPath() {
    const { hash } = window.location;
    return hash.split('#/')[1]?.split('?')[0] || '';
  }

  public safeParseJSON(json: string): { [key: string]: any } {
    if (!_.isString(json)) return {};
    let result = {};
    try {
      result = JSON.parse(json);
    } catch (e) {
      result = {};
    }

    return result;
  }

  public safeRedirect(url: string) {
    if (!/^https?:\/\//.test(url)) return;

    const link = document.createElement('a');
    link.setAttribute('rel', 'noopener noreferrer');
    link.setAttribute('href', url);
    // Tips: 不加这行，在firefox上执行link.click()时无反应
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
  }

  public getOriginHost(url: string) {
    const configUrl = new URL(url);
    let ret = configUrl.host;
    [
      window.ONEID_PUBLIC_ORIGINAL_ADMIN_SITE,
      window.ONEID_PUBLIC_ORIGINAL_IDP_SITE,
      window.ONEID_PUBLIC_ORIGINAL_PORTAL_SITE,
    ].forEach((originUrl) => {
      const originUrlConfig = new URL(originUrl);
      if (_.includes(configUrl.host, originUrlConfig.host)) {
        ret = originUrlConfig.host;
      }
    });
    return ret;
  }
  public replaceUrlContent(
    url: string,
    config: {
      domain?: string; // TIPS:如果domain是空字符串会将domain设置为空
      host?: string;
      search?: string;
      pathname?: string;
      hash?: string;
      protocol?: string;
    } = {},
  ) {
    if (!url) return '';
    const configUrl = new URL(url);

    const { pathname, protocol, search, hash } = window.location;

    configUrl.host = this.getOriginHost(url);
    if (_.isString(config.domain) && config.domain) {
      configUrl.host = `${config.domain}.${configUrl.host}`;
    }
    // 域名部分不能设置成空字符串，其他部分允许设置成空字符串
    return `${config.protocol || protocol}//${config.host || configUrl.host}${
      _.has(config, 'pathname') ? config.pathname : pathname
    }${_.has(config, 'search') ? config.search : search}${
      _.has(config, 'hash') ? config.hash : hash
    }`;
  }

  // 跳转到经过 自定义域名处理过的 idp 地址
  public redirectToIdp(qs?: string, path?: string) {
    this.safeRedirect(JSRuntime.getIdpSite(qs, path));
  }

  /** 跳转到 SaaS 版的 idp 地址 */
  public redirectToOriginalIdp(qs?: string, path?: string) {
    this.safeRedirect(JSRuntime.getOriginalIdpSite(qs, path));
  }

  public redirectToPortal(qs?: string, path?: string) {
    this.safeRedirect(
      `${JSRuntime.accountOauth2Site}/v1/auth?goto=${JSRuntime.getPortalSite(qs, path)}`,
    );
  }

  /**
   * 跳转到管理后台 - 不带企业域名
   * @param qs 参数
   * @param path 页面地址
   */
  public redirectToAdmin(qs?: string, path?: string) {
    this.safeRedirect(JSRuntime.getAdminSite(qs, path));
  }

  /**
   * 跳转到已登录企业的管理后台地址 - 自动带企业域名
   */
  public async redirectToAccountAdmin() {
    const accountInfo = await CommonAccountApiV3.getAccountInfo();
    if (JSRuntime.adminSite.startsWith(`https://${accountInfo.domain}`)) {
      this.safeRedirect(JSRuntime.getAdminSite());
      return;
    }
    const domain = JSRuntime.adminSite.replace(/^(https?:\/\/)/, `$1${accountInfo.domain}.`);
    this.safeRedirect(`${domain}/admin/`);
  }

  public redirectToOfficial() {
    this.safeRedirect(JSRuntime.officialSite);
  }

  /**
   * 进入二次认证页面
   * @param certify 认证检查信息
   * @param redirect_uri 认证成功后地址
   */
  public redirectToCertify(certify: ICertifyApi, redirect_uri?: string) {
    let certifyUrl = `${JSRuntime.originalIdpSite}/#/certify?state=${certify.state}`;
    redirect_uri && (certifyUrl += `&redirect_uri=${encodeURIComponent(redirect_uri)}`);
    certify.loginMobile && (certifyUrl += `&mobile=${encodeURIComponent(certify.loginMobile)}`);
    this.safeRedirect(certifyUrl);
  }

  /**
   * 将utc时间转换为dayjs格式
   * TIPS: 后端返回的utc时间会统一成时间戳，现在需要同时兼容。
   * @param utcTime 10位时间戳 或 13位时间戳 或 'YYYY-MM-DD HH:mm:ss'
   */
  public utcToLocale(utcTime: number | string) {
    try {
      let time = utcTime;
      if (typeof utcTime === 'number' && String(utcTime).length === 10) {
        time = utcTime * 1000;
      }
      return dayjs.utc(time).local();
    } catch (err) {
      return dayjs();
    }
  }

  public safeDownload = _.debounce(
    async (
      url: string,
      configs?: { needCookie?: boolean; unionId?: string; ignoreLang?: boolean },
    ) => {
      if (!url) return;
      const { needCookie, unionId, ignoreLang } = configs || {};
      if (needCookie && unionId) {
        await AdminApiV2.setCosCookie(unionId);
      }
      const link = document.createElement('a');
      link.href = `${url}${ignoreLang || !url.startsWith('http') ? '' : `${url.includes('?') ? '&' : '?'}lang=${i18n.language}`}`;
      link.style.display = 'none';
      // 浏览器会自动获取文件名
      link.setAttribute('download', '');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    DEBOUNCE_DEFAULT_SECOND,
  );

  public computedOverflowLabel = (prevLabel = '', limit: number) => {
    let len = 0;
    let nextLabel = '';
    let label = '';
    if (_.isString(prevLabel)) {
      label = prevLabel;
    } else if (_.isNumber(prevLabel) && !_.isNaN(prevLabel)) {
      label = `${prevLabel}`;
    }
    const hasOverflow = label.split('').some((str) => {
      const isCN = /[\u4e00-\u9fa5]/gm.test(str);
      if (len < limit) nextLabel += str;
      const isOverflow = len >= limit;
      len += isCN ? 2 : 1;
      return isOverflow;
    });

    return { hasOverflow, label: hasOverflow ? `${nextLabel}...` : nextLabel };
  };

  public async postPopupRedirectMsg(url: string) {
    // 从 document.referrer 获取可信的父窗口 origin，避免向任意域名发送敏感的 redirectUrl
    let targetOrigin: string | null = null;
    try {
      const ref = document.referrer;
      if (ref) {
        const parsed = new URL(ref);
        if (parsed.protocol === 'https:' || parsed.protocol === 'http:') {
          targetOrigin = parsed.origin;
        }
      }
    } catch {
      // 非法 referrer，忽略
    }
    if (!targetOrigin) return;
    JSRuntime.popupPostMessage(
      {
        type: 'EIDAuthnSuccess',
        payload: { redirectUrl: url },
      },
      targetOrigin,
    );
  }

  public getFileMD5(file: File) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onload = () => {
        resolve(MD5(lib.WordArray.create(reader.result as any)).toString() || '');
      };
      reader.onerror = (err) => {
        reject(err);
      };
    });
  }
  public fileToBase64(file: File) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        resolve(reader.result);
      };
      reader.onerror = (err) => {
        reject(err);
      };
    });
  }

  // 用于网络地址或者 blobUrl 字符串转为 Blob 对象 axios@0.27 有bug 所以用的 XMLHttpRequest
  public urlToBlob(url: string) {
    return new Promise((resolve, reject) => {
      const req = new XMLHttpRequest();
      req.open('GET', url, true);
      req.responseType = 'blob';

      req.addEventListener('load', () => {
        const blob = req.response;
        resolve(blob);
      });

      req.addEventListener('error', (e) => {
        reject(e);
      });

      req.send();
    });
  }

  public phoneHide(phone?: string) {
    if (!_.isString(phone)) return;
    const hidePhone = phone.replace(/(\d{3})\d{4}(\d{0})/, '$1****$2');
    return hidePhone;
  }

  public focusFirstInput() {
    const inputs = document.getElementsByTagName('input');
    if (inputs && inputs.length > 0) {
      const firstInput = inputs[0];
      firstInput.focus();
    }
  }

  public copyText(text: string) {
    if (!text) {
      return;
    }
    const input = document.createElement('textarea');
    input.value = text;
    document.body.appendChild(input);
    input.select();
    // 执行 copy 命令
    if (document.execCommand('copy')) {
      document.execCommand('copy');
    }
    document.body.removeChild(input);
  }

  /**
   * 密码安全管控需要，改方法只在私有化环境下使用。其余地方切换成接口调用
   * 生成随机密码
   * 密码规则：包含0-9数字、大小写字母，英文字符，四种类型都必须至少包含一个；
   * @param len 密码长度 Random password length MUST at least 3!
   */
  public genRandomPassword = (len: number): string => {
    if (len < 3) {
      return '';
    }
    // ASCII code: 48 => 0
    const digits = Array(10)
      .fill('')
      .map((x: string, index: number) => String.fromCharCode(index + 48));
    // ASCII code: 65 => A
    const upperLetters = Array(26)
      .fill('')
      .map((x: string, index: number) => String.fromCharCode(index + 65));
    // ASCII code: 97 => a
    const lowerLetters = Array(26)
      .fill('')
      .map((x: string, index: number) => String.fromCharCode(index + 97));
    const symbols = '!"#$%&\'()*+,-./:;<=>?@[]^_`{|}~'.split('');
    const allLetters = [...digits, ...upperLetters, ...lowerLetters, ...symbols];
    const randomLen = len - 4;
    // 除了必须包含的4种类型字符，其他随机获取
    const passwordArr = _.sampleSize(allLetters, randomLen);
    // 分别追加三种类型的字符，保证符合密码规则
    passwordArr.push(
      digits[Math.floor(Math.random() * 10)],
      upperLetters[Math.floor(Math.random() * 26)],
      lowerLetters[Math.floor(Math.random() * 26)],
      symbols[Math.floor(Math.random() * 31)],
    );
    // 打乱字符
    return _.shuffle(passwordArr).join('');
  };

  /**
   * 根据配置数据生成自定义样式style
   * @param brands 自定义样式信息
   */
  public genCustomStyle = (brands: IBrands) => {
    const { backgroundType, position, imageUrl, fillingType, color } = brands;
    const style: { [key: string]: string } = {};

    if (position && position !== LOGIN_POSITIONS.DEFAULT) {
      style.justifyContent = POSITIONS[position];
    }

    if (backgroundType === BACKGROUND_TYPES.IMAGE && imageUrl) {
      const backgroundStyle = {
        backgroundImage: `url(${imageUrl})`,
        backgroundSize: fillingType?.toLowerCase() || 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
      };
      Object.assign(style, backgroundStyle);
    }

    if (backgroundType === BACKGROUND_TYPES.COLOR && color) {
      const backgroundStyle = {
        backgroundImage: 'none',
        backgroundColor: color,
      };
      Object.assign(style, backgroundStyle);
    }

    return style;
  };

  // 增加不带hash值的className， eg: 'SSOLogin_wrapper_hnMqn' =>  'SSOLogin_wrapper_hnMqn SSOLogin_wrapper'
  public addUnHashedClass = (classNameMap: { [key: string]: string }) =>
    Object.keys(classNameMap).reduce((prev: { [key: string]: string }, className: string) => {
      const prevClassName = classNameMap[className] || '';
      const newClassName = prevClassName.split('_').slice(0, 2).join('-');
      if (!_.isString(prevClassName)) return { ...prev, [className]: prevClassName };

      return { ...prev, [className]: `${prevClassName} ${newClassName}` };
    }, {});

  // 用于切换 样式 或者注入 样式
  resetThemeStyle(styleString: string) {
    const preStyleNode = document.querySelector('style#eid-theme-style');
    if (
      preStyleNode?.parentElement &&
      _.isFunction(_.get(preStyleNode, 'parentElement.removeChild'))
    ) {
      preStyleNode.parentElement.removeChild(preStyleNode);
    }
    const style = document.createElement('style');
    style.innerHTML = styleString;
    style.id = 'eid-theme-style'; // 不要改这个值
    document.head.appendChild(style);
    return () => {
      style.remove();
    };
  }

  clearSessionStore() {
    sessionStorage.clear();
  }

  // 查看错误响应中是否包含 安全合规-内容风险 检查出的错误，需要处理的response格式如下：
  // {
  //   "errCode": "E0010217",
  //   "errMessage": "[{\"msg\":\"内容不合法，请修改后重新保存\",\"desc\":\"name\"},{\"msg\":\"内容不合法，请修改后重新保存\",\"desc\":\"username\"}]",
  //   "id": "c18df1f23e9579a7f605aec32464de37"
  // }
  public hasInvalidContent(err: { data: { errCode: string; errMessage: string } }) {
    return err?.data?.errCode === INVALID_CONTENT_ERROR_CODE;
  }
  public formatInvalidContent(err: { data: { errCode?: string; errMessage?: string } }) {
    const getInvalidKeyAndMsg = (errMessage?: string) => {
      const invalidData = {};
      const firstErrKey = ''; // 第一个错误字段key，用于滚动到表单处

      const result = this.safeParseJSON(errMessage || '') || [];
      if (_.isArray(result)) {
        result.forEach((item: { msg: string; desc: string }) => {
          if (item?.desc && item?.msg) {
            invalidData[item.desc] = [{ type: 'error', message: item.msg }];
          }
        });
      }
      return { invalidData, firstErrKey };
    };

    if (err?.data?.errCode === INVALID_CONTENT_ERROR_CODE) {
      const { invalidData, firstErrKey } = getInvalidKeyAndMsg(err?.data?.errMessage);
      return {
        invalidData,
        firstErrKey,
      };
    }
    return { invalidData: {}, firstErrKey: '' };
  }

  /**
   * 是否是合法的手机号
   * @param phoneNum 手机号
   * @description 国内虚拟手机号：+86 160为前缀，11位，例+86 16000911111
   * @description 海外虚拟手机号：+852 1239为前缀，8或10位，例+852 1239111111
   */
  public isValidPhoneNumber(phoneNum: string) {
    if (!phoneNum) return false;

    const reg = /(^\+86 160[0-9]{8}$)|(^\+852 1239[0-9]{4}$)|(^\+852 1239[0-9]{6}$)/;

    return isValidPhoneNumber(phoneNum) || reg.test(phoneNum);
  }

  // 邮箱新校验格式
  public isValidEmail(email: string) {
    if (!email) return false;

    const regStr = "a-zA-Z0-9!#$%&'*+/=?^_`{|}~-";
    const reg = new RegExp(`^[${regStr}]+$`);
    const [prefix, suffix] = email.split('@');
    const subStr = (prefix.split('.') || []).concat(suffix?.split('.') || []);
    const newValidate = !!(prefix && suffix && subStr.every((str) => reg.test(str)));

    return newValidate;
  }

  public addAccountChangeListener(getCurrentUnionId: () => string | undefined) {
    // 移动端不轮询
    if (
      JSRuntime.isMobileDevice ||
      JSRuntime.isWechat ||
      JSRuntime.isWxworkBrowser ||
      JSRuntime.isWechatMiniProgram
    )
      return;

    try {
      if (!window.sessionWorker) {
        window.sessionWorker = new SharedWorker(
          // @ts-ignore
          new URL('./sessionWorker.js', import.meta.url),
        );
      }

      // 此时已经连接上worker
      window.sessionWorker?.port?.postMessage(JSRuntime.accountOauth2Site);

      window.sessionWorker.port.onmessage = (e: Event) => {
        const currUnionId = getCurrentUnionId();
        if (!currUnionId) return;

        const sub = _.get(e, 'data.sub');
        const active = _.get(e, 'data.active');
        // NOTICE 登录态失效或者是切换账户导致信息变更，告知用户。
        if (!active || (sub && sub !== currUnionId)) {
          window.sessionWorker?.port?.close();
          const dialogAlert = DialogPlugin.alert({
            header: i18n.t('登录态发生变更'),
            placement: 'center',
            body: i18n.t('当前登录身份已经发生变更，页面会在本窗口关闭后刷新'),
            confirmBtn: i18n.t('立即刷新'),
            closeOnOverlayClick: false,
            onConfirm: () => {
              window.location.reload();
              dialogAlert.hide();
            },
            onClose: () => {
              window.location.reload();
              dialogAlert.hide();
            },
          });
        }
      };
    } catch (err) {
      /* empty */
    }

    return () => {
      window.sessionWorker?.port?.close();
    };
  }

  // 加密信息
  public encryptObj(key: string, kid: string, obj: Record<string, any>) {
    const objJson = JSON.stringify({ ...obj, nonce: uuidv4() });
    const encrypt = new JSEncrypt();
    encrypt.setPublicKey(key);
    const encryptStr = encrypt.encrypt(objJson) || ''; // 加密
    return `${encryptStr}.${kid}`;
  }

  // 获取cookie
  public getCookie = (key: string) => {
    const cookieStr = document.cookie;
    if (_.isString(cookieStr)) {
      const cookieArr = cookieStr?.split(';');
      for (let i = 0; i < cookieArr.length; i++) {
        const c = cookieArr[i].trim();
        if (c.indexOf(key) == 0) {
          return c.substring(key.length + 1, c.length);
        }
      }
    }
    return '';
  };

  // 上报信息需要屏蔽的字段
  private FILTER_PARAMS_KEYS = ['mobile', 'email'];

  public joinUrl = (baseURL?: string, path?: string) => {
    if (!path) return baseURL;
    const url = new URL(path, baseURL);
    return url.href;
  };

  /**
   * 接口成功上报信息
   * @param response 响应信息
   */
  public aegisSuccessInfo = (response: Record<string, any>) => {
    const { config, data, status, headers } = response || {};
    const requestUrl = this.joinUrl(config?.baseURL, config?.url);
    const xTraceId = headers?.['x-trace-id'] || '';
    const reportHeaders = {
      'x-trace-id': xTraceId,
    };

    const reportData = `Response:
      method => ${config.method};
      url => ${requestUrl};
      status => ${status};
      data => ${JSONStringify(maskData({ data: JSONParse(config.data), keys: this.FILTER_PARAMS_KEYS }))};
      params => ${JSONStringify(maskData({ data: config.params, keys: this.FILTER_PARAMS_KEYS }))};
      response => ${JSONStringify(maskData({ data, keys: this.FILTER_PARAMS_KEYS }))};
      headers => ${JSONStringify(reportHeaders)};
    `;

    return reportData;
  };

  /**
   * 接口失败上报信息
   * @param response 响应信息
   */
  public aegisErrorInfo = (error: TApiError) => {
    const { response } = error;
    const { config, headers, data: errData } = response || {};
    const requestUrl = this.joinUrl(config?.baseURL, config?.url);
    const xTraceId = headers?.['x-trace-id'] || '';
    const err = {
      status: Number(error.status || 0),
      errCode: errData?.errCode || error.code,
      errMessage: errData?.errMessage || error.message || getUnknownMistake(),
    };
    const reportData = `Response Error:
      url => ${requestUrl};
      error => ${JSONStringify(err)};
      x-trace-id: ${xTraceId};
    `;
    return reportData;
  };

  public isWhiteRedirect = (redirectUrl: string) => {
    // 白名单合法后缀列表，使用 endsWith 精确匹配，防止正则 g 标志 lastIndex 状态错乱
    // 以及类似 evil.tencent.com.evil.com 的后缀绕过攻击
    const REDIRECT_WHITE_SUFFIXES = ['.tencent.com', '.tencentcs.com', '.qq.com'];
    const currUrl = new URL(window.location.href);
    const originUrl = new URL(decodeURIComponent(redirectUrl));
    const host = originUrl.host.toLowerCase();
    // TIPS：白名单判断标准，要么是腾讯域名，要么是浏览器url的host等于redirectUrl的host（主要解决私有化情况）
    return (
      REDIRECT_WHITE_SUFFIXES.some((suffix) => host.endsWith(suffix)) ||
      currUrl.host === originUrl.host
    );
  };

  public handleNoAuth = (landingPage?: string) => {
    if (JSRuntime.thirdLandingPage) {
      if (this.isWhiteRedirect(JSRuntime.thirdLandingPage)) {
        this.safeRedirect(JSRuntime.thirdLandingPage);
        return;
      }
      this.redirectToIdp('', '/login');
    } else {
      const method = window.localStorage.getItem('authenticationMethod');
      const redirectUrl = landingPage || window.location.href;
      const redUrl = `?redirectUrl=${window.encodeURIComponent(redirectUrl)}`;
      if (method === AUTHENTICATION_METHOD.ENTERPRISE && JSRuntime.isCustomDomain) {
        this.redirectToIdp(redUrl, '/sso_login/identity');
      } else {
        this.redirectToOriginalIdp(redUrl);
      }
    }
  };

  /**
   * 拍平树形结构
   * @param list 数据源
   * @param key 获取子节点集合的key
   * @returns Array<any>
   */
  public flattenTree = <T = any>(list: Array<T> = [], key = 'children'): Array<T> => {
    return list.flatMap((item: T) => {
      const children = (item as any)?.[key] || [];
      return [item, ...this.flattenTree(children, key)];
    });
  };

  /**
   * 根据 action_group_code 从权限数据中获取无权限的报错信息
   * @param agc string
   * @return agc对应的权限错误消息
   */
  public getPermsErrMsgByCode = async (agc: string): Promise<string> => {
    if (!agc) return '';
    const { action_groups = [] } = await RolesApi.getActionGroups();

    const flatList = this.flattenTree(action_groups);

    let msg = '';
    const paths = agc.split('/');
    while (paths.length) {
      const path = paths.join('/');
      const authInfo = flatList.find((item) => item.action_group_code === path);
      if (authInfo?.name) {
        msg = authInfo.name;
        break;
      }
      paths.pop();
    }
    return i18n.t('无{msg}访问权限，请联系管理员', { msg });
  };

  /** 获取SSO登录链接 */
  public getSSOLoginLink(appId: string) {
    return `${JSRuntime.apiSite}/v1/${APINames.AUTHN}/sso/${appId}`;
  }

  /**
   * 验证是否为合法的Base64字符串
   * @param str 待验证字符串
   * @returns boolean
   */
  isValidBase64(str: string) {
    return /^[A-Za-z0-9\-_=+/]*$/i.test(str);
  }

  /**
   * Base64解码
   * @param str 待解码字符串
   * @param options 解码选项
   * @returns 解码后的字符串
   */
  base64Decode(str: string, options: { isSafe?: boolean } = {}) {
    try {
      if (!this.isValidBase64(str)) return str;
      const { isSafe = false } = options;

      // 处理URL安全的Base64格式成标准的base64格式
      if (isSafe) {
        str = str.replace(/-/g, '+').replace(/_/g, '/');
        // 自动添加填充
        if (str.length % 4 !== 0) {
          const padLength = 4 - (str.length % 4);
          str += '='.repeat(padLength);
        }
      }
      return enc.Base64.parse(str).toString(enc.Utf8);
    } catch (error) {
      return str;
    }
  }
}

export default new Utils();
